import { useNavigate, useLocation, Outlet, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../store/useAuthStore';
import { usePermission } from '../hooks/usePermission';
import { usePermissionStore } from '../store/usePermissionStore';
import { useLanguage } from '../hooks/useLanguage';
import { Breadcrumbs } from '../components/ui/Breadcrumbs';
import { DashboardShortcuts } from '../components/ui/KeyboardShortcuts';
import {
  Settings,
  LogOut,
  ChevronDown,
  Building2,
  PlusCircle,
  ShieldAlert,
  Lock,
  Languages,
} from 'lucide-react';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarTrigger,
  SidebarInset
} from '../components/ui/sidebar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { ErrorBoundary } from '../components/ErrorBoundary';
import { OfflineIndicator } from '../components/OfflineIndicator';
import { NAVIGATION_CONFIG, filterCounterModeNavigation, filterGroceryModeNavigation } from '../config/navigation';

export function DashboardLayout({ children }: { children?: React.ReactNode }) {
  const { user, business, logout, isImpersonating, bootstrapError } = useAuthStore();
  const { hasPermission } = usePermission();
  const { isModuleEnabled } = usePermissionStore();
  const { language, toggleLanguage } = useLanguage();
  const { t } = useTranslation();
  const canAddProduct = hasPermission('inventory.edit') && isModuleEnabled('inventory');
  const navigate = useNavigate();
  const location = useLocation();

  const displayName = `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim() || 'User';
  const initials = displayName.split(' ').filter(Boolean).map((n: string) => n[0]).join('').toUpperCase() || 'U';

  const myWorkPaths = new Set(['/hr/my-attendance', '/hr/my-leaves', '/hr/my-payslips', '/hr/assistance']);

  const isCounterModeEnabled = false; // Toggle via tenant segment flag
  const isGroceryModeEnabled = false; // Toggle via tenant segment flag
  const navGroups = filterGroceryModeNavigation(
    filterCounterModeNavigation(NAVIGATION_CONFIG, isCounterModeEnabled),
    isGroceryModeEnabled
  )
    .map(group => ({
      ...group,
      items: group.items.map(item => {
        const isSelfWork = myWorkPaths.has(item.path);
        const hasModulePermission = hasPermission(item.permission);
        // The user is permitted but the module isn't on their plan → show it dimmed with a
        // lock so higher-tier features stay discoverable (clicking routes to the upgrade view).
        const isLocked = !isSelfWork && hasModulePermission && !isModuleEnabled(item.moduleCode);

        return {
          ...item,
          isLocked,
          isVisible: isSelfWork || hasModulePermission
        };
      }).filter(item => item.isVisible)
    }))
    .filter(group => group.items.length > 0);

  // Single source for titles of routes that aren't in NAVIGATION_CONFIG.
  const EXTRA_TITLES: Record<string, string> = {
    '/settings': 'Business Settings',
    '/team': 'Team Management',
    '/business-profile': 'Business Profile',
  };
  const getPageTitle = () => {
    const path = location.pathname;
    if (EXTRA_TITLES[path]) return EXTRA_TITLES[path];
    const item = NAVIGATION_CONFIG.flatMap(g => g.items).find(i => i.path === path);
    return item?.label || "Dashboard";
  };

  const NAV_LABEL_KEYS: Record<string, string> = {
    'Dashboard': 'nav.dashboard',
    'SALES': 'nav.groupSales',
    'FINANCE': 'nav.groupFinance',
    'INVENTORY': 'nav.groupInventory',
    'MY WORK': 'nav.groupMyWork',
    'TEAM & HR': 'nav.groupTeamHr',
    'ADMIN': 'nav.groupAdmin',
  };

  const ITEM_LABEL_KEYS: Record<string, string> = {
    'Dashboard': 'nav.dashboard',
    'Point of Sale': 'nav.pos',
    'Invoices': 'nav.invoices',
    'Partners': 'nav.partners',
    'Digital Khata': 'nav.khata',
    'Expenses': 'nav.expenses',
    'Business Reports': 'nav.reports',
    'Aging & Analytics': 'nav.agingReports',
    'Inventory': 'nav.inventory',
    'Purchase': 'nav.purchase',
    'Damaged Goods': 'nav.damagedGoods',
    'My Attendance': 'nav.myAttendance',
    'My Leaves': 'nav.myLeaves',
    'My Payslips': 'nav.myPayslips',
    'HR Assistance': 'nav.hrAssistance',
    'HR Management': 'nav.hr',
    'Projects': 'nav.projects',
    'Timesheets': 'nav.timesheets',
    'Organization': 'nav.organization',
    'Approval Dashboard': 'nav.approvalDashboard',
    'Approval Inbox': 'nav.approvalInbox',
    'Approval Analytics': 'nav.approvalAnalytics',
  };

  return (
    <SidebarProvider>
      <DashboardShortcuts />
      <div data-testid="app-shell" className="flex min-h-screen w-full bg-background transition-colors">
        <Sidebar className="border-r border-sidebar-border bg-sidebar shadow-xs transition-colors">
          <SidebarHeader className="border-b border-sidebar-border bg-sidebar">
            <div className="flex items-center gap-3 px-3 py-3 sm:py-4">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary text-primary-foreground text-sm font-semibold shadow-xs shrink-0">
                B
              </div>
              <div className="flex flex-col">
                <span className="text-sm font-semibold text-foreground leading-none">BizCore</span>
                <span className="text-[11px] text-muted-foreground tracking-wide mt-1 hidden sm:block">Business</span>
              </div>
            </div>
          </SidebarHeader>

          <SidebarContent className="bg-sidebar pt-4">
            <div className="space-y-6 pt-2">
              {navGroups.map((group) => (
                <SidebarGroup key={group.label}>
                  <SidebarGroupLabel className="text-xs font-semibold text-muted-foreground px-6 mb-1 tracking-wide">
                    {t(NAV_LABEL_KEYS[group.label] || group.label)}
                  </SidebarGroupLabel>
                  <SidebarGroupContent>
                    <SidebarMenu className="px-3 gap-1">
                      {group.items.map((item) => {
                        const isActive = location.pathname === item.path;
                        return (
                          <SidebarMenuItem key={item.path}>
                            <SidebarMenuButton
                              asChild
                              isActive={isActive}
                              tooltip={item.isLocked ? `${item.label} (locked)` : item.label}
                              className={`
                                h-9 px-3 rounded-md transition-colors
                                focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring
                                ${isActive
                                  ? 'bg-primary text-primary-foreground shadow-xs'
                                  : 'text-muted-foreground hover:bg-accent hover:text-foreground'
                                }
                              `}
                            >
                              <Link
                                to={item.path}
                                data-testid={`nav-link-${item.path}`}
                                className="flex items-center gap-3"
                                aria-label={item.isLocked ? `${item.label} — locked, upgrade to unlock` : `Navigate to ${item.label}`}
                                title={item.isLocked ? 'Upgrade to unlock this module' : undefined}
                              >
                                <item.icon size={16} className={isActive ? "text-primary-foreground" : "text-muted-foreground"} />
                                <span className={`text-sm font-medium ${item.isLocked ? 'opacity-60' : ''}`}>{t(ITEM_LABEL_KEYS[item.label] || item.label)}</span>
                                {item.isLocked && <Lock size={12} className="ml-auto shrink-0 text-muted-foreground" />}
                              </Link>
                            </SidebarMenuButton>
                          </SidebarMenuItem>
                        );
                      })}
                    </SidebarMenu>
                  </SidebarGroupContent>
                </SidebarGroup>
              ))}
            </div>
          </SidebarContent>

          <SidebarFooter className="p-2 sm:p-3 border-t border-sidebar-border bg-sidebar">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="user-menu" className="flex w-full items-center gap-2 sm:gap-3 px-2 sm:px-3 py-2 sm:py-2.5 rounded-md hover:bg-accent transition-colors group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                  <Avatar className="h-8 w-8 rounded-md shrink-0">
                    <AvatarFallback className="bg-primary-subtle text-primary-subtle-foreground font-semibold text-xs rounded-md">
                      {initials}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex flex-1 flex-col items-start overflow-hidden min-w-0">
                    <span className="text-sm font-medium text-foreground truncate w-full">{displayName}</span>
                    <span className="text-xs text-muted-foreground truncate w-full hidden sm:block">{user?.email}</span>
                  </div>
                  <ChevronDown size={14} className="text-muted-foreground transition-colors shrink-0" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 rounded-lg p-1.5 shadow-md border border-border bg-popover">
                {hasPermission('settings.view') && (
                <DropdownMenuItem onClick={() => navigate('/settings')} className="rounded-md px-3 py-2 cursor-pointer hover:bg-accent focus:bg-accent">
                  <Settings className="mr-2.5 h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium">Settings</span>
                </DropdownMenuItem>
                )}
                <DropdownMenuSeparator className="my-1 bg-border" />
                <DropdownMenuItem data-testid="sign-out" onClick={logout} className="rounded-md px-3 py-2 cursor-pointer text-danger hover:bg-danger-bg focus:bg-danger-bg">
                  <LogOut className="mr-2.5 h-4 w-4" />
                  <span className="text-sm font-medium">Sign out</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarFooter>
        </Sidebar>

        <SidebarInset className="flex flex-col flex-1 overflow-hidden">
          {isImpersonating && (
            <div role="alert" className="sticky top-0 z-20 flex items-center justify-center gap-2 bg-primary-active px-4 py-2 text-center text-sm font-medium text-primary-foreground shadow-xs">
              <ShieldAlert size={16} className="shrink-0" />
              <span>
                Impersonating — you are viewing this account as a SuperAdmin
                {user?.email ? <span className="font-semibold"> ({user.email})</span> : null}
                {user?.role != null && String(user.role) !== '' ? <span className="font-normal"> · role {String(user.role)}</span> : null}
              </span>
              <button
                onClick={() => {
                  if (window.confirm('Exit impersonation and return to your SuperAdmin session?')) logout();
                }}
                className="ml-2 rounded-md bg-white/20 px-2 py-0.5 text-xs font-semibold hover:bg-white/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
              >
                Exit
              </button>
            </div>
          )}
          {bootstrapError && (
            <div role="alert" className="flex items-center justify-between gap-3 bg-warning-bg border-b border-warning/30 px-4 py-2 text-sm text-warning-foreground">
              <span>{bootstrapError}</span>
              <button
                onClick={() => window.location.reload()}
                className="rounded-md bg-warning/15 px-3 py-1 text-xs font-semibold text-warning-foreground hover:bg-warning/25 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-warning focus-visible:ring-offset-2"
              >
                Retry
              </button>
            </div>
          )}
          <header className="h-14 sm:h-16 border-b border-border bg-card sticky top-0 z-10 px-3 sm:px-4 md:px-6 flex items-center justify-between transition-colors">
            <div className="flex items-center gap-2 sm:gap-4 min-w-0">
              <SidebarTrigger className="h-9 w-9 rounded-md hover:bg-accent transition-colors text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring shrink-0" />
              <div className="flex flex-col min-w-0">
                <h1 data-testid="page-title" className="text-base sm:text-lg font-semibold tracking-tight text-foreground leading-none truncate">{t(ITEM_LABEL_KEYS[getPageTitle()] || getPageTitle())}</h1>
                <div className="flex items-center gap-1.5 mt-1">
                  <Building2 size={11} className="text-muted-foreground shrink-0" />
                  <span className="max-w-[120px] sm:max-w-[200px] truncate text-xs font-medium text-muted-foreground" title={business?.name || undefined}>{business?.name || 'Loading...'}</span>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-1 sm:gap-2 shrink-0">
              <OfflineIndicator />
              <button
                onClick={toggleLanguage}
                aria-label={language === 'en' ? 'Switch to Nepali' : 'Switch to English'}
                className="flex items-center gap-1.5 px-2 sm:px-3 h-9 rounded-md text-sm font-medium text-muted-foreground hover:bg-accent hover:text-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                title={language === 'en' ? 'नेपालीमा स्विच गर्नुहोस्' : 'Switch to English'}
              >
                <Languages size={15} />
                <span className="hidden sm:inline">{language === 'en' ? 'NE' : 'EN'}</span>
              </button>
              <div className="h-6 w-px bg-border mx-0.5 sm:mx-1 hidden sm:block" />

              {canAddProduct && (
                <button
                  onClick={() => navigate('/inventory?action=add-product')}
                  className="hidden sm:flex items-center gap-2 px-4 h-9 bg-primary text-primary-foreground rounded-md font-medium text-sm hover:bg-primary-hover transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                >
                  <PlusCircle size={15} />
                  <span>New Product</span>
                </button>
              )}
            </div>
          </header>

          <main className="flex-1 overflow-y-auto p-3 sm:p-4 md:p-8 bg-background transition-colors">
            <ErrorBoundary>
              <Breadcrumbs />
              {children || <Outlet />}
            </ErrorBoundary>
          </main>
        </SidebarInset>
      </div>
    </SidebarProvider>
  );
}
